'use client';

import { useMemo } from 'react';
import config from 'config';

import { Flex, Stack, Text, useTheme } from '@chakra-ui/react';

import { useDashboardSectionAccess } from 'hooks';

import { filterFooterMenuItems } from 'utils/helpers/dashboardNav';

import { SocialLinkType } from 'types';

import { Link } from 'ui';
import { Container } from '../Container';

const { socialLinks, footerMenu, copyrightLabel, poweredByText, linkAuthor } =
  config;

type FooterMenuItem = {
  isExternal: boolean;
  href: string;
  label: string;
  id?: string;
};

const Footer = () => {
  const {
    colors: {
      palette: {
        components: { footer }
      }
    }
  } = useTheme();
  const { isSectionEnabled } = useDashboardSectionAccess();

  const visibleFooterMenu = useMemo(
    () => filterFooterMenuItems<FooterMenuItem>(footerMenu, isSectionEnabled),
    [isSectionEnabled]
  );

  const textStyles = {
    fontSize: '14px',
    fontFamily: 'main',
    color: footer.textColor,
    fontWeight: 400,
    lineHeight: 1.5
  };

  const iconStyles = {
    '& > svg': {
      width: '21px',
      height: '21px'
    }
  };

  const linkHover = {
    borderBottom: `1px solid transparent`,

    '&:hover': {
      borderBottom: `1px solid ${footer.textColor}`
    }
  };

  return (
    <Flex
      as='footer'
      width='100vw'
      position='relative'
      left='50%'
      transform='translateX(-50%)'
      bg={footer.bgColor}
      id='footer'
      _before={{
        content: '""',
        position: 'absolute',
        width: '100%',
        height: '2px',
        top: 0,
        left: 0,
        background: footer.dividerColor
      }}
    >
      <Container px={{ base: '15px', md: '25px' }}>
        <Stack
          w='100%'
          alignItems='center'
          justifyContent='center'
          pt='30px'
          pb='25px'
          gap={{ base: '20px', md: '60px' }}
        >
          <Flex
            flexDirection={{
              base: 'column',
              lg: 'row'
            }}
            w='100%'
            alignItems='center'
            justifyContent={{ base: 'center', lg: 'space-between' }}
            wrap='wrap'
            gap='25px'
          >
            <Flex gap='36px' wrap='wrap'>
              {socialLinks.map(({ link, Icon }: SocialLinkType) => (
                <Link
                  key={link}
                  color={footer.iconColor}
                  to={link}
                  sx={iconStyles}
                  external
                >
                  <Icon fill={footer.iconColor} />
                </Link>
              ))}
            </Flex>
            <Flex
              flexDirection={{ base: 'column', sm: 'row' }}
              justifyContent='center'
              flexWrap='wrap'
            >
              <Text
                sx={textStyles}
                textAlign='center'
                mb={{ base: '20px', sm: '0' }}
              >
                {`© ${new Date().getFullYear()} ${copyrightLabel}`}
              </Text>

              <Flex>
                {visibleFooterMenu.map(
                  (item: FooterMenuItem, index: number) => {
                    const isFirstChild = index === 0;
                    return (
                      <Flex key={item.id ?? item.label}>
                        <Text
                          px={2.5}
                          sx={textStyles}
                          display={{
                            base: isFirstChild ? 'none' : 'block',
                            sm: 'block'
                          }}
                        >
                          |
                        </Text>
                        <Link
                          external={item.isExternal}
                          to={item.href}
                          sx={{
                            ...textStyles,
                            ...linkHover,
                            fontWeight: 'semibold'
                          }}
                        >
                          {item.label}
                        </Link>
                      </Flex>
                    );
                  }
                )}
              </Flex>
            </Flex>
          </Flex>

          <Link external to={linkAuthor}>
            <Text _hover={linkHover} sx={{ ...textStyles, fontSize: '12px' }}>
              {poweredByText}
            </Text>
          </Link>
        </Stack>
      </Container>
    </Flex>
  );
};

export default Footer;
